import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { InformationBoxComponent } from '@principle-theorem/ng-principle-shared';
import { NgDialogModule } from '@principle-theorem/ng-shared';
import {
  CampaignCostEstimate,
  getCampaignScopeToXLXSMap,
  TimezoneResolver,
} from '@principle-theorem/principle-core';
import {
  ICampaignEntryReportResult,
  IMarketingCampaign,
  IMarketingCampaignRun,
} from '@principle-theorem/principle-core/interfaces';
import {
  CampaignEntryEligibility,
  CampaignEntryReport,
  ICampaignEntryCSVContext,
} from '@principle-theorem/reporting';
import {
  asyncForEach,
  firstValueFrom,
  getError,
  shareReplayCold,
  WithRef,
  XSLXImporterExporter,
} from '@principle-theorem/shared';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import {
  CampaignRunEnrolmentEstimatesComponent,
  ICampaignEnrolment,
} from '../campaign-run-enrolment-estimates/campaign-run-enrolment-estimates.component';

export interface ICampaignRunUploadDialogRequest {
  campaign: WithRef<IMarketingCampaign>;
  campaignRun: WithRef<IMarketingCampaignRun>;
  uploadedFiles: File[];
}

export interface ICampaignRunUploadDialogResponse {
  campaign: WithRef<IMarketingCampaign>;
  campaignRun: WithRef<IMarketingCampaignRun>;
  entries: ICampaignEntryReportResult[];
}

@Component({
  selector: 'pr-campaign-run-upload-dialog',
  templateUrl: './campaign-run-upload-dialog.component.html',
  styleUrl: './campaign-run-upload-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CampaignRunEnrolmentEstimatesComponent,
    InformationBoxComponent,
    MatProgressSpinner,
    NgDialogModule,
  ],
})
export class CampaignRunUploadDialogComponent {
  private _dialogRef =
    inject<
      MatDialogRef<
        CampaignRunUploadDialogComponent,
        ICampaignRunUploadDialogResponse
      >
    >(MatDialogRef);
  data = inject<ICampaignRunUploadDialogRequest>(MAT_DIALOG_DATA);
  loading = signal<boolean>(true);
  errorMessage$ = new BehaviorSubject<string | undefined>(undefined);
  enrolment$: Observable<ICampaignEnrolment | undefined>;
  isDisabled$: Observable<boolean>;

  constructor() {
    this.enrolment$ = of(this.data.uploadedFiles).pipe(
      tap(() => {
        this.loading.set(true);
        this.errorMessage$.next(undefined);
      }),
      switchMap((uploadedFiles) =>
        this._loadFiles(
          this.data.campaign,
          this.data.campaignRun,
          uploadedFiles
        )
      ),
      catchError((error) => {
        this.errorMessage$.next(getError(error));
        return of(undefined);
      }),
      tap(() => this.loading.set(false)),
      shareReplayCold()
    );
    this.isDisabled$ = this.enrolment$.pipe(
      map((enrolment) => !enrolment?.entries?.length)
    );
  }

  async submit(): Promise<void> {
    const enrolment = await firstValueFrom(this.enrolment$);
    if (!enrolment?.entries?.length) {
      return;
    }
    this._dialogRef.close({
      campaign: this.data.campaign,
      campaignRun: this.data.campaignRun,
      entries: enrolment.entries,
    });
  }

  async _loadFiles(
    campaign: WithRef<IMarketingCampaign>,
    _campaignRun: WithRef<IMarketingCampaignRun>,
    uploadedFiles: File[]
  ): Promise<ICampaignEnrolment | undefined> {
    if (!uploadedFiles.length) {
      return;
    }

    const results = await asyncForEach(uploadedFiles, async (file) =>
      this._entriesFromFile(campaign, file)
    );
    const potentialEntries = results.flat();

    const entries = await CampaignEntryEligibility.filterEligibleEntries(
      campaign,
      potentialEntries
    );

    const estimates = await CampaignCostEstimate.calculateEstimates(
      campaign,
      entries,
      campaign.maximumEntriesPerRun
    );

    return {
      entries,
      skippedEntries: potentialEntries.length - entries.length,
      campaignEntriesInPreview: 0,
      estimates,
    };
  }

  private async _entriesFromFile(
    campaign: WithRef<IMarketingCampaign>,
    file: File
  ): Promise<ICampaignEntryReportResult[]> {
    const xlsxMap = getCampaignScopeToXLXSMap(campaign.brandRef);

    const potentialEntries = await new XSLXImporterExporter().parse(
      file,
      xlsxMap[campaign.scope]
    );

    const context: ICampaignEntryCSVContext = {
      scope: campaign.scope,
      timezone: await TimezoneResolver.fromBrandRef(campaign.brandRef),
    };

    return CampaignEntryReport.getCSVEntries(context, potentialEntries);
  }
}
